import { MaternalLabs } from '@/features/labs/MaternalLabs';
import { LabTrends } from '@/features/labs/LabTrends';
import { usePermissions } from '@/features/permissions';
import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';

import { clientApi } from '@/config/axios';
import { TrendFigures } from './TrendFigures';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { type Context, useImagingApi, useImagingCapabilities, useImagingQuery } from './api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
export function PregnancyImaging({ pregnancyId, initialTab = 'maternal' }: { pregnancyId: string; initialTab?: 'maternal' | 'fetal' }) {
  const cap = useImagingCapabilities();
  const { can } = usePermissions();
  const { base } = useImagingApi();
  const canRead = cap.data?.permissions.some((p) => ['prepare_imaging', 'read_imaging'].includes(p));
  const data = useImagingQuery<Context>(`/pregnancies/${pregnancyId}/trends`, !!canRead);
  const [view, setView] = useState<'flowsheet' | 'values' | 'percentiles'>('flowsheet');
  const [fetus, setFetus] = useState('1');
  const [percentileUrl, setPercentileUrl] = useState<string | null>(null);
  const [percentileError, setPercentileError] = useState(false);
  useEffect(() => {
    if (!canRead || view !== 'percentiles') return;
    let active = true;
    let url: string | null = null;
    void clientApi.get(`${base}/pregnancies/${pregnancyId}/percentile-chart`, {
      params: { fetus }, responseType: 'blob',
    }).then(({ data: svg }) => {
      if (!active) return;
      url = URL.createObjectURL(svg);
      setPercentileUrl(url);
      setPercentileError(false);
    }).catch(() => { if (active) setPercentileError(true); });
    return () => { active = false; if (url) URL.revokeObjectURL(url); setPercentileUrl(null); };
  }, [base, canRead, fetus, pregnancyId, view]);
  if (!canRead && !can('read_patient')) return null;
  return (
    <Tabs defaultValue={canRead ? initialTab : 'maternal'}>
      <TabsList>
        <TabsTrigger value="maternal">Maternal flowsheet</TabsTrigger>
        {canRead && <TabsTrigger value="fetal">Fetal flowsheet</TabsTrigger>}
      </TabsList>
      {(canRead ? ['maternal', 'fetal'] : ['maternal']).map((kind) => {
        const series = (data.data?.series || []).filter((s) =>
          kind === 'fetal' ? s.fetus !== null : s.fetus === null,
        );
        const dates = [...new Set(series.flatMap((s) => s.points.map((p) => p.date)))].sort();
        return (
          <TabsContent key={kind} value={kind}>
            <div className="flex flex-col gap-4">
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" onClick={() => setView('flowsheet')}>View flowsheet</Button>
                <Button variant="outline" onClick={() => setView('values')}>Value trends</Button>
                {kind === 'fetal' && <Button variant="outline" onClick={() => setView('percentiles')}>Percentile trends</Button>}
              </div>
              {kind === 'maternal' && (view === 'values' ? <LabTrends pregnancyId={pregnancyId} /> : <MaternalLabs pregnancyId={pregnancyId} />)}
              {data.isError ? (
                <p>Unable to load imaging observations. Retry from Imaging.</p>
              ) : view === 'percentiles' && kind === 'fetal' ? (
                <div>
                  <div className="mb-3 max-w-48">
                    <Select value={fetus} onValueChange={setFetus}>
                      <SelectTrigger aria-label="Fetus for percentile trends"><SelectValue /></SelectTrigger>
                      <SelectContent>{Array.from({ length: data.data?.dating?.fetus_count || 1 }, (_, index) => (
                        <SelectItem key={index + 1} value={String(index + 1)}>Fetus {index + 1}</SelectItem>
                      ))}</SelectContent>
                    </Select>
                  </div>
                  {percentileError ? <p>Unable to load percentile trends.</p> : percentileUrl ? <img src={percentileUrl} alt={`Fetus ${fetus} reviewed biometry percentile trends by anatomy`} /> : <p>Loading percentile trends…</p>}
                  <p>Reference screening only. Verify pregnancy dating, measurement source and reference suitability before clinical use.</p>
                </div>
              ) : view === 'values' ? (
                <TrendFigures series={series} />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Measurement</TableHead>
                      {dates.map((d) => (
                        <TableHead key={d}>{d}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {series.map((s) => (
                      <TableRow key={s.key}>
                        <TableCell>
                          {s.fetus ? `Fetus ${s.fetus} · ` : ''}
                          {s.metric} ({s.unit})
                        </TableCell>
                        {dates.map((d) => (
                          <TableCell key={d}>
                            {s.points
                              .filter((p) => p.date === d)
                              .map((p) => (
                                <div key={p.study_id}>
                                  <Link to="/imaging/$studyId" params={{ studyId: p.study_id }}>
                                    {p.value.toFixed(2)}
                                  </Link>
                                  <small className="block">
                                    {p.ga_days === null
                                      ? 'Dating unavailable'
                                      : `${Math.floor(p.ga_days / 7)}w ${p.ga_days % 7}d`}
                                  </small>
                                </div>
                              ))}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {!series.length && !data.isLoading && <p>No reviewed {kind} imaging observations for this pregnancy.</p>}
              <p>
                Open a source study to select figures for its draft report. Values remain scoped to this pregnancy and
                fetus.
              </p>
            </div>
          </TabsContent>
        );
      })}
    </Tabs>
  );
}
